import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { ClipType } from '@air/api/types';
import { clipCanOpenInCanvas } from '@air/feature-edit-canvas';

import {
  EDIT_MODE_OPTION_CROP,
  EDIT_MODE_OPTION_CUSTOM_PROMPT,
  EDIT_MODE_OPTION_EXTEND_BACKGROUND,
  EDIT_MODE_OPTION_REMOVE_OBJECT,
  EDIT_MODE_OPTION_REPLACE_BACKGROUND,
  EDIT_MODE_OPTION_ROTATE,
  EDIT_MODE_OPTION_TRANSLATE,
  EDIT_MODE_OPTION_UPSCALE,
  type EditModeOption,
} from '~/components/EditMode/constants';
import { useEditMode } from '~/components/EditMode/EditModeProvider';
import { useEditModeStatus } from '~/components/EditMode/hooks/useEditModeStatus';
import { useRemoveAssetTransformationTasksForVersion } from '~/components/EditMode/hooks/useRemoveAssetTransformationTasksForVersion';
import { useHasAssetTransformsFlag } from '~/hooks/flags/useHasAssetTransformsFlag';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import {
  centralizedClipIdSelector,
  centralizedClipSelector,
  centralizedClipTypeSelector,
} from '~/store/centralizedClip/selectors';
import { canCreateCanvasInWorkspace } from '~/utils/permissions/workspacePermissions';

export type UseEditModeOptionsReturn = {
  editModeOptions: EditModeOption[];
  currentEditModeOption: EditModeOption | null;
  editModeOptionActive: boolean;
};

export const useEditModeOptions = (): UseEditModeOptionsReturn => {
  const assetVersionId = useSelector(centralizedClipIdSelector);
  const previousAssetVersionIdRef = useRef<string | undefined>();
  const clip = useSelector(centralizedClipSelector);
  const clipType = useSelector(centralizedClipTypeSelector);
  const { assetTransforms } = useHasAssetTransformsFlag();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { currentEditModeOption, setCurrentEditModeOption } = useEditMode();
  const { setStatusIdle } = useEditModeStatus();
  const { removeTasks } = useRemoveAssetTransformationTasksForVersion();

  // Translate runs on a canvas rather than as a transform on the asset, so it is gated on canvas
  // creation rights instead of an assetTransforms addon flag, and is dropped rather than upsold
  // when unavailable.
  const canTranslateInCanvas =
    !!clip &&
    (clipType === ClipType.photo || clipType === ClipType.animated) &&
    canCreateCanvasInWorkspace(workspacePermissions) &&
    clipCanOpenInCanvas(clip);

  const editModeOptions: EditModeOption[] = [
    {
      ...EDIT_MODE_OPTION_CUSTOM_PROMPT,
      enabled: assetTransforms.promptEnabled,
    },
    {
      ...EDIT_MODE_OPTION_CROP,
      enabled: assetTransforms.cropEnabled,
    },
    {
      ...EDIT_MODE_OPTION_ROTATE,
      enabled: assetTransforms.orientEnabled,
    },
    {
      ...EDIT_MODE_OPTION_REMOVE_OBJECT,
      enabled: assetTransforms.removeObjectEnabled,
    },
    {
      ...EDIT_MODE_OPTION_REPLACE_BACKGROUND,
      enabled: assetTransforms.replaceBackgroundEnabled,
    },
    {
      ...EDIT_MODE_OPTION_EXTEND_BACKGROUND,
      enabled: assetTransforms.outpaintEnabled,
    },
    ...(canTranslateInCanvas ? [{ ...EDIT_MODE_OPTION_TRANSLATE, enabled: true }] : []),
    {
      ...EDIT_MODE_OPTION_UPSCALE,
      enabled: assetTransforms.upscaleEnabled,
    },
  ];

  useEffect(() => {
    const previousAssetVersionId = previousAssetVersionIdRef.current;

    if (previousAssetVersionId && previousAssetVersionId !== assetVersionId) {
      setCurrentEditModeOption(null);
      setStatusIdle();
      removeTasks({ assetVersionId: previousAssetVersionId });
    }

    previousAssetVersionIdRef.current = assetVersionId;
  }, [assetVersionId, setCurrentEditModeOption, setStatusIdle, removeTasks]);

  return {
    editModeOptions,
    currentEditModeOption,
    editModeOptionActive: currentEditModeOption !== null,
  };
};
